import { PointColorType } from '@pnext/three-loader';
import { atom } from 'jotai';
import { atomFamily, atomWithStorage, createJSONStorage } from 'jotai/utils';
import type { LayerSettings, LayersQuery } from '../graphql/graphql';

export type tLayerSettings = Omit<LayerSettings, '__typename'>;
export type LayerData = LayersQuery['layers'][number];

// Atom to store all layers for the current project
export const layersAtom = atom<LayerData[]>([]);

const layerSelectionStorageKey = (projectId: string) => `@actegon.gis.layers.selected.${projectId}`;

// Atom family to store the selected layer IDs per project with localStorage persistence
const storage = createJSONStorage<string[]>(() => sessionStorage);
export const selectedLayerIdsAtom = atomFamily((projectId: string) =>
  atomWithStorage<string[]>(layerSelectionStorageKey(projectId), [], storage),
);

// Derived atom to get the selected layers (computed from layers and selectedLayerIds)
export const selectedLayersAtom = atomFamily((projectId: string) =>
  atom((get) => {
    const layers = get(layersAtom);
    const selectedIds = get(selectedLayerIdsAtom(projectId));
    return layers.filter((layer) => selectedIds.includes(layer.id));
  }),
);

// Write-only atom to toggle layer selection
export const toggleLayerSelectionAtom = atomFamily((projectId: string) =>
  atom(null, (get, set, layerId: string) => {
    const selectionAtom = selectedLayerIdsAtom(projectId);
    const currentSelection = get(selectionAtom);
    const isSelected = currentSelection.includes(layerId);

    if (isSelected) {
      // Remove from selection
      set(
        selectionAtom,
        currentSelection.filter((id) => id !== layerId),
      );
    } else {
      // Add to selection
      set(selectionAtom, [...currentSelection, layerId]);
    }
  }),
);

// Write-only atom to set all layers (typically called after fetching from API)
export const setLayersAtom = atom(null, (_get, set, layers: LayerData[]) => {
  set(layersAtom, layers);
});

// Write-only atom to clear layer selection
export const clearLayerSelectionAtom = atomFamily((projectId: string) =>
  atom(null, (_get, set) => {
    set(selectedLayerIdsAtom(projectId), []);
  }),
);

// Write-only atom to select all layers
export const selectAllLayersAtom = atomFamily((projectId: string) =>
  atom(null, (get, set) => {
    const layers = get(layersAtom);
    set(
      selectedLayerIdsAtom(projectId),
      layers.map((layer) => layer.id),
    );
  }),
);

// Derived atom to check if a specific layer is selected
export const isLayerSelectedAtom = atomFamily((projectId: string) =>
  atom((get) => (layerId: string) => {
    const selectedIds = get(selectedLayerIdsAtom(projectId));
    return selectedIds.includes(layerId);
  }),
);

export const layerSettingsStorageKey = (layerId: string) => `@actegon.gis.layer.settings.${layerId}`;
export const layerSettingsAtom = atomFamily((layerId: string) =>
  atomWithStorage<tLayerSettings | null>(
    layerSettingsStorageKey(layerId),
    null,
    createJSONStorage<tLayerSettings | null>(() => localStorage),
  ),
);

export const defaultLayerSettings: tLayerSettings = {
  pointCloudMaterialColorSource: PointColorType.DEPTH as any,
  pointCloudMaterialColor: 'GRAYSCALE' as const,
  pointCloudFixedColor: '#FF0000',
  modelIsWireframe: false,
  modelDefaultColor: '#777777',
  modelOpacity: 1.0,
  modelColorBy: 'DEFAULT',
  modelClassColors: [],
  landxmlCategoryColors: {
    surface: '#a08060',
    alignment: '#e53935',
    parcel: '#43a047',
    breakline: '#787878',
    pipe: '#8b6914',
    structure: '#607d8b',
  },
  landxmlSurfaceColorMode: 'SOLID',
  landxmlSurfaceColormap: 'SPECTRAL' as const,
  pointCloudClasses: new Array(256).fill(null).map(() => ({ isEnabled: true, color: '#000000' })),
  imagePointColor: '#FF69B4',
  imageBrightness: 0,
  imageContrast: 0,
  imageSaturation: 0,
  vectorDefaultColor: '#000000',
  vectorDefaultOpacity: 1.0,
  vectorIsWireframe: false,
  vectorDefaultPointSize: 0.15,
  // 'SOLID' (default) | 'ELEVATION' | 'SLOPE'. Mirrors the LandXML
  // surface coloring controls so users can drape a colormap over any
  // vector TIN (rainfall TIN sources, hand-drawn surfaces, imported
  // triangle GeoJSONs).
  vectorSurfaceColorMode: 'SOLID',
  vectorSurfaceColormap: 'SPECTRAL' as const,
  rasterOpacity: 0.8,
  rasterContrast: 0,
  rasterSaturation: 0,
  rasterBrightness: 0,
  scalarRangeMin: null,
  scalarRangeMax: null,
};

/**
 * Returns layer-specific default settings. For deviation layers (those with
 * metadata.scalarAttribute === 'Deviation') the color source defaults to
 * PointColorType.CUSTOM_SCALAR (51) and the gradient to SPECTRAL.
 */
export const layerDefaultSettings = (layer: LayerData | null | undefined): tLayerSettings => {
  const isDeviationLayer = (layer?.metadata as any)?.scalarAttribute === 'Deviation';
  if (!isDeviationLayer) return defaultLayerSettings;
  return {
    ...defaultLayerSettings,
    pointCloudMaterialColorSource: PointColorType.CUSTOM_SCALAR as any,
    pointCloudMaterialColor: 'SPECTRAL' as const,
  };
};

// Create a stable atom that takes layerIds as a parameter
export const layerSettingsManyAtom = atom((get) => (layerIds: string[]) => {
  return layerIds.reduce(
    (acc, layerId) => {
      const layers = get(layersAtom);
      const layer = layers.find((layer) => layer.id === layerId);
      const settings = get(layerSettingsAtom(layerId));
      acc[layerId] = settings || layer?.settings || null;
      return acc;
    },
    {} as Record<string, tLayerSettings | null>,
  );
});
